/**
 * The ONE table of money-field caps and price-warning rules (KAN-1061 /
 * RD-BILL-020). Every money field takes its `maxValue` from here — never a
 * literal — and the database enforces the same caps (migration
 * `*_money_amount_caps.sql`; `moneyLimits.sqlTwin.test.js` keeps the two in
 * step). Pure: no React, no react-intl (see moneyWarning.js for why).
 *
 * Caps by kind:
 *   linePrice     any per-unit price a person types: job services, chemicals
 *                 and extras, pricing an extra, invoice / manual / estimate
 *                 lines, catalog and special prices, platform plan prices.
 *   creditRecord  one recorded customer credit — the same ceiling the
 *                 credit-grant RPC already enforces server-side.
 *   weeklyRate    a recurring customer's weekly rate (the Convert flow) — the
 *                 same 99,999.99 as linePrice, its own key so the DB twin
 *                 (`*_customers_weekly_rate_money_cap.sql`) can name it.
 *   creditLimit   the company's credit-limit settings.
 * Fields capped by the data itself (a discount ≤ the subtotal, removing /
 * transferring credit ≤ the available balance) keep their dynamic cap.
 */
import { MONEY_SOFT_WARNING_DEFAULT } from '../validation/moneyWarning';

export const MONEY_LIMITS = Object.freeze({
  linePrice: 99999.99,
  weeklyRate: 99999.99,
  creditRecord: 25000,
  creditLimit: 25000,
});

/**
 * "Far from usual": above `high`× or below `low`× the item's usual price
 * (its catalog price, or the last price charged for that extra). With no
 * usual price, only the flat amount warns.
 */
export const PRICE_VS_USUAL = Object.freeze({
  high: 3,
  low: 1 / 3,
  flat: MONEY_SOFT_WARNING_DEFAULT,
});

function toNumber(v) {
  if (v === '' || v == null) return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Compare a typed price to the item's usual price. Advisory only — nothing
 * here blocks a save.
 *
 * - Blank never warns (unanswered); zero never warns (a deliberate answer —
 *   a comped visit, a warranty call).
 * - A usual price that is missing, ≤ 0, or itself above the flat amount is
 *   not trusted as a reference; only the flat amount decides.
 * - The flat amount is a floor: a price inside the usual band but above
 *   `flat` still warns.
 *
 * @param {string|number} value - the typed price.
 * @param {string|number|null} usual - the item's usual price, if known.
 * @param {{ high?: number, low?: number, flat?: number }} [rule]
 * @returns {null | { kind: 'aboveUsual'|'belowUsual'|'aboveFlat', usual: number|null, ratio: number|null, flat: number }}
 */
export function comparePriceToUsual(value, usual, rule = PRICE_VS_USUAL) {
  const { high = PRICE_VS_USUAL.high, low = PRICE_VS_USUAL.low, flat = PRICE_VS_USUAL.flat } = rule;
  const v = toNumber(value);
  if (v == null || v === 0) return null;
  const u = toNumber(usual);
  const trusted = u != null && u > 0 && u <= flat;
  if (trusted) {
    const ratio = v / u;
    if (ratio > high) return { kind: 'aboveUsual', usual: u, ratio, flat };
    if (ratio < low) return { kind: 'belowUsual', usual: u, ratio, flat };
  }
  if (v > flat) return { kind: 'aboveFlat', usual: trusted ? u : null, ratio: trusted ? v / u : null, flat };
  return null;
}
